import type { LiveCommand } from '@ccs/live-contract';

type Params = Record<string, unknown>;

const RELAY_LABEL: Record<string, string> = {
  tower_fan: '塔风机',
  tower_pump: '塔水泵',
  loop_pump: '循环水泵',
  desorber_reflux: '解吸塔回流泵',
  absorber_reflux: '吸收塔回流泵',
  makeup_valve: '补液电磁阀',
  gas_heat: '进气加热',
};

const INVERTER_LABEL: Record<string, string> = {
  poor_pump: '贫液泵',
  rich_pump: '富液泵',
  air: '鼓风机',
};

const FLOW_UNIT: Record<string, string> = {
  poor_pump: 'L/h',
  air: 'm³/h',
};

const ERROR_TEXT: Record<string, string> = {
  bridge_offline: '工控机不在线',
  ack_timeout: '设备未确认',
  device_not_found: '设备不存在',
  device_disabled: '设备已停用',
  control_disabled: '远程控制已关闭',
  forbidden: '没有控制权限',
  rate_limited: '操作太频繁，请 2 秒后再试',
  invalid_request: '命令参数不合法',
  acquisition_not_running: '采集未开始，无法控制',
  device_stale: '实时数据已陈旧，暂停控制',
};

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asNumber(value: unknown): number | null {
  const numeric = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(numeric) ? numeric : null;
}

function frequency(value: unknown): string {
  const hz = asNumber(value);
  return hz === null ? '' : `${hz.toFixed(1)} Hz`;
}

function temperature(value: unknown): string {
  const celsius = asNumber(value);
  return celsius === null ? '' : `${Math.round(celsius * 10) / 10}`;
}

function flow(value: unknown, target: string): string {
  const rate = asNumber(value);
  if (rate === null) return '';
  return `${Math.round(rate * 100) / 100} ${FLOW_UNIT[target] ?? 'L/h'}`;
}

function label(map: Record<string, string>, key: string, fallback: string): string {
  return map[key] ?? (key ? `${fallback}（${key}）` : fallback);
}

/** 把一条控制命令翻译成简洁的中文操作说明。 */
export function describeCommand(command: Pick<LiveCommand, 'action' | 'params'>): string {
  const params: Params = command.params ?? {};

  switch (command.action) {
    case 'relay.set': {
      const device = asText(params.device);
      const on = params.on === true;
      if (device === 'makeup_valve') return `${on ? '打开' : '关闭'}补液电磁阀`;
      if (device === 'gas_heat') return `进气加热${on ? '开启' : '关闭'}`;
      return `${on ? '启动' : '停止'}${label(RELAY_LABEL, device, '未知设备')}`;
    }
    case 'inverter.start': {
      const target = asText(params.target);
      const name = label(INVERTER_LABEL, target, '未知变频器');
      if (params.mode === 'pid') {
        const setpoint = flow(params.setpoint, target);
        return `${name} PID 启动${setpoint ? `（设定 ${setpoint}）` : ''}`;
      }
      const hz = frequency(params.hz);
      return hz ? `${name}按 ${hz} 启动` : `${name}启动`;
    }
    case 'inverter.set_hz': {
      const target = asText(params.target);
      const name = label(INVERTER_LABEL, target, '未知变频器');
      const hz = frequency(params.hz);
      return hz ? `${name}频率改为 ${hz}` : `${name}频率已调整`;
    }
    case 'inverter.set_setpoint': {
      const target = asText(params.target);
      const name = label(INVERTER_LABEL, target, '未知变频器');
      const setpoint = flow(params.setpoint, target);
      return setpoint ? `${name}流量设定改为 ${setpoint}` : `${name}流量设定已调整`;
    }
    case 'inverter.stop':
      return `${label(INVERTER_LABEL, asText(params.target), '未知变频器')}停止`;
    case 'heating.configure': {
      const enabled = params.enabled === true;
      const upper = temperature(params.upper_c);
      const lower = temperature(params.lower_c);
      const range = lower && upper ? `（${lower}–${upper} ℃）` : '';
      return `进气加热${enabled ? '开启' : '关闭'}${range}`;
    }
    case 'all.stop':
      return '全部设备停止';
    case 'acq.start':
      return '开始采集';
    case 'acq.stop':
      return '停止采集';
    default:
      return command.action;
  }
}

/** 把服务端错误码或桥接中文原因转成可读的中文说明。 */
export function describeCommandError(error: string | null | undefined): string | null {
  const text = error?.trim();
  if (!text) return null;
  if (ERROR_TEXT[text]) return ERROR_TEXT[text];
  if (/^http_\d{3}$/.test(text)) return '服务暂时不可用，请稍后再试';
  // 桥接返回的拒绝原因本身已经是中文，直接展示
  if (/[\u4e00-\u9fff]/.test(text)) return text;
  return '命令未执行';
}